import { Menu, Search, User, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo_header.png";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { SubscriptionProgress } from "@/components/subscription/SubscriptionProgress";
import { NotificationBell } from "@/components/notifications/NotificationBell";

interface HeaderProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

interface Profile {
  full_name?: string | null;
  email?: string;
  avatar_url?: string | null;
}

export function Header({ searchQuery, setSearchQuery }: HeaderProps) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [headerAvatarUrl, setHeaderAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    async function loadProfile() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();

        setProfile(profileData);
        setHeaderAvatarUrl(profileData?.avatar_url || null);

        // Fallback: se o perfil não tiver foto, tenta pegar do paciente mais recente.
        if (!profileData?.avatar_url) {
          const { data: patient } = await supabase
            .from("patients_onboarding")
            .select("avatar_url")
            .eq("owner_id", user.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (patient?.avatar_url) setHeaderAvatarUrl(patient.avatar_url);
        }
      }
    }
    loadProfile();
  }, []);

  async function logout() {
    await supabase.auth.signOut();
    nav("/auth");
  }

  return (
    <>
      {/* Desktop Header */}
      <header className="hidden md:block h-[73px] border-b bg-white">
        <div className="h-full px-5 flex items-center justify-between gap-3">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar pacientes, documentos... (Enter para buscar)"
                className="pl-9 pr-4 h-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchQuery.trim()) {
                    e.preventDefault();
                    nav(`/app/busca?q=${encodeURIComponent(searchQuery)}`);
                    setSearchQuery("");
                  }
                }}
              />
            </div>
          </div>

          <div className="flex-1 max-w-sm">
            <SubscriptionProgress />
          </div>

          <div className="flex items-center gap-3">
            <NotificationBell />
            {/* User Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:opacity-80 transition">
                  <Avatar className="h-8 w-8">
                    {headerAvatarUrl && <AvatarImage src={headerAvatarUrl} alt="Avatar" />}
                    <AvatarFallback>{profile?.full_name?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {profile?.full_name || "Usuário"}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {profile?.email || ""}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/app/minha-conta")}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Minha conta</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-40 bg-white border-b">
        <div className="h-14 px-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} aria-label="Abrir menu">
            <Menu className="h-6 w-6" />
          </button>
          <img src={logo} alt="Elinia" className="h-auto w-auto" />

          <div className="flex items-center gap-3">
            <NotificationBell />
            {/* User Menu Dropdown - Mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:opacity-80 transition">
                  <Avatar className="h-8 w-8">
                    {headerAvatarUrl && <AvatarImage src={headerAvatarUrl} alt="Avatar" />}
                    <AvatarFallback>{profile?.full_name?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {profile?.full_name || "Usuário"}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {profile?.email || ""}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/app/minha-conta")}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Minha conta</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="px-3 pb-2">
          <SubscriptionProgress />
        </div>

        {open && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white border-r p-3 space-y-1">
              <div className="px-2 py-3 border-b">
                <div className="font-semibold">Menu</div>
              </div>
              <nav className="space-y-1" onClick={() => setOpen(false)}>
                <NavLink to="/app" className="block rounded-lg px-3 py-2 hover:bg-muted">
                  Início
                </NavLink>
                <NavLink to="/app/pacientes" className="block rounded-lg px-3 py-2 hover:bg-muted">
                  Pacientes
                </NavLink>
                <NavLink to="/app/aplicativos" className="block rounded-lg px-3 py-2 hover:bg-muted">
                  Aplicativos
                </NavLink>
                <NavLink to="/app/categorias" className="block rounded-lg px-3 py-2 hover:bg-muted">
                  Categorias
                </NavLink>
                <NavLink to="/app/estatisticas" className="block rounded-lg px-3 py-2 hover:bg-muted">
                  Estatísticas
                </NavLink>
                <div className="pt-3 mt-3 border-t" />
                <NavLink to="/app/conta" className="block rounded-lg px-3 py-2 hover:bg-muted">
                  Minha conta
                </NavLink>
                <button onClick={logout} className="w-full text-left rounded-lg px-3 py-2 hover:bg-muted">
                  Sair
                </button>
              </nav>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
